import { watchEffect } from 'vue';

/**
 * Lightweight document-head manager for the SPA. `index.html` carries the
 * static defaults (first paint + dumb crawlers); this keeps <title> and the
 * OpenGraph / Twitter tags in sync as the user navigates client-side and as
 * async page data (product, article) resolves.
 *
 * Note: tags are set client-side. Search engines that execute JS (Google) and
 * many link unfurlers will pick these up, but scrapers that read raw HTML only
 * see the index.html defaults — proper per-URL previews need SSR/prerendering.
 */

export const SITE_NAME = 'PlayStock';

// Absolute origin used for the default OG image + canonical fallbacks.
const SITE_ORIGIN = (import.meta.env.VITE_APP_URL as string | undefined)?.replace(/\/$/, '') || 'https://playstock.ink';

const DEFAULTS = {
  title: 'PlayStock — Game Keys Marketplace',
  description:
    'PlayStock — buy game keys instantly at unbeatable prices. Secure checkout, instant delivery, and thousands of PC, Steam, and console titles.',
  image: `${SITE_ORIGIN}/og-image.png`,
  type: 'website',
};

export interface MetaInput {
  title?: string;
  description?: string;
  image?: string | null;
  url?: string;
  type?: string;
}

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/** Imperatively apply meta tags, filling any blanks with site defaults. */
export function applyMeta(input: MetaInput = {}): void {
  const title = input.title?.trim() || DEFAULTS.title;
  const description = input.description?.trim() || DEFAULTS.description;
  const image = input.image || DEFAULTS.image;
  const type = input.type || DEFAULTS.type;
  const url = input.url || window.location.href;

  document.title = title;
  setMeta('name', 'description', description);

  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:image', image);
  setMeta('property', 'og:url', url);
  setMeta('property', 'og:type', type);
  setMeta('property', 'og:site_name', SITE_NAME);

  setMeta('name', 'twitter:card', image ? 'summary_large_image' : 'summary');
  setMeta('name', 'twitter:title', title);
  setMeta('name', 'twitter:description', description);
  setMeta('name', 'twitter:image', image);

  setCanonical(url);
}

/**
 * Reactive head manager for a page. Pass a getter returning the page's meta;
 * it re-applies whenever the reactive sources change (e.g. once a product or
 * article finishes loading). Return {} to fall back to site defaults.
 */
export function useMeta(source: () => MetaInput): void {
  watchEffect(() => applyMeta(source()));
}

/**
 * Build an OG-friendly description from arbitrary page text: strip HTML,
 * collapse whitespace, and truncate to a single readable line.
 */
export function metaDescription(raw: string | null | undefined, max = 200): string {
  if (!raw) return '';
  const text = raw
    .replace(/<[^>]+>/g, ' ')
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}…`;
}
